/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useViewedScreen } from "@packages/analytics";
import { useConsent } from "@packages/consent";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HallowDataTestId } from "@tools/playwright/global";

import { cssMediaQueryMinWidthTablet } from "../../../../emotion";
import type { ThemeType } from "../../../../global/emotion";
import { numericPercentages, spacing } from "../../../../global/emotion";
import { Button } from "../../_base/_core/Button/Button.emotion";
import type { TextProps } from "../../_base/_core/Text/Text.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { Ratings } from "../../marketing/Ratings";
import { Reviews } from "../../marketing/Reviews";
import type { StackProps } from "../layout/Stack/Stack.emotion";
import { Stack } from "../layout/Stack/Stack.emotion";
import { AppleAuthButton } from "./AppleAuthButton.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";
import { GoogleAuthButton } from "./GoogleAuthButton.emotion";

const containerStyle: Interpolation = {
  padding: spacing.ml,
  width: numericPercentages[100],
};
const cssRatings: Interpolation<ThemeType> = (theme) => ({
  paddingTop: theme.spacing.ms,
  [cssMediaQueryMinWidthTablet]: {
    paddingTop: theme.spacing.l,
  },
});
const cssReviews: Interpolation<ThemeType> = (theme) => ({
  paddingTop: theme.spacing.ms,
  [cssMediaQueryMinWidthTablet]: {
    paddingTop: theme.spacing.l,
  },
});
const legalStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  marginTop: spacing.ms,
  textAlign: "center",
  width: numericPercentages[100],
});
const legalLinkStyle: Interpolation = {
  textDecoration: "underline",
};

export type AuthMainFlowProps = AuthFlowProps & {
  isRatings?: boolean;
  isReviews?: boolean;
  legalCSS?: Interpolation;
  legalProps?: Partial<TextProps>;
} & Pick<StackProps, "className">;

export const AuthMainFlow = ({
  isRatings,
  isReviews,
  onAuth,
  setFlow,
  email,
  legalCSS = {},
  legalProps,
  allowedMethods,
  className,
}: AuthMainFlowProps) => {
  const t = useTranslations();
  const { countryCode } = useConsent();
  const allowPhoneFlow =
    countryCode === "US" && allowedMethods.includes("phone");
  const initialSimpleFlow =
    countryCode === "US" && allowPhoneFlow && !email ? "phone" : "email";

  useViewedScreen({ screen: "choose_auth_option" });

  return (
    <GoogleOAuthProvider
      clientId={process.env.NEXT_PUBLIC_ACCESS_GOOGLE_CLIENT_ID}
    >
      <Stack gap={spacing.ms} css={containerStyle} className={className}>
        {(allowPhoneFlow || allowedMethods.includes("email")) && (
          <Button
            isFullWidth
            size="l"
            onClick={() => setFlow(initialSimpleFlow)}
            data-testid={HallowDataTestId.AuthContinueWithPhoneOrEmailButton}
          >
            {t(
              allowPhoneFlow
                ? "sms_sign_up_continue_phone_or_email"
                : "launch_continue_with_email",
            )}
          </Button>
        )}
        {allowedMethods.includes("google") && (
          <GoogleAuthButton onAuth={onAuth} />
        )}
        {allowedMethods.includes("apple") && (
          <AppleAuthButton onAuth={onAuth} />
        )}
        <Text
          css={[legalStyle, legalCSS]}
          {...({
            size: "s",
            type: "body",
            ...legalProps,
          } as TextProps)}
        >
          {t.rich("launch_privacy_and_terms_links", {
            privacy: (chunk) => (
              <a
                href="https://hallow.com/privacy-policy"
                target="_blank"
                css={legalLinkStyle}
              >
                {chunk}
              </a>
            ),
            terms: (chunk) => (
              <a
                href="https://hallow.com/terms-of-service"
                target="_blank"
                css={legalLinkStyle}
              >
                {chunk}
              </a>
            ),
          })}
        </Text>
        {isRatings && <Ratings css={cssRatings} />}
        {isReviews && <Reviews css={cssReviews} />}
      </Stack>
    </GoogleOAuthProvider>
  );
};

AuthMainFlow.displayName = "AuthMainFlow";
